import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../../../utils/formatters';
import { formatDate } from '../../../utils/dateUtils';
import {
  configService,
  StorePricing,
  StorePricingCreate,
  StorePricingUpdate,
} from '../../../services/configService';
import * as siteService from '../../../services/siteService';
import { authService } from '../../../services/auth/authService';
import { StoreSiteType } from '../../../types/site';

interface SiteRateFormData {
  site_type_id?: number;
  description: string;
  price: number;
  discount: number;
  effective_date: string;
  end_date: string;
  active: boolean;
}

const SiteRateCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [siteRates, setSiteRates] = useState<StorePricing[]>([]);
  const [siteTypes, setSiteTypes] = useState<StoreSiteType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRate, setEditingRate] = useState<StorePricing | null>(null);
  const [formData, setFormData] = useState<SiteRateFormData>({
    site_type_id: undefined,
    description: '',
    price: 0,
    discount: 0,
    effective_date: '',
    end_date: '',
    active: true,
  });
  const [saving, setSaving] = useState(false);

  const storeId = authService.getStoreId();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rates, types] = await Promise.all([
        configService.getStorePricing(storeId, false),
        siteService.getStoreSiteTypes(storeId)
      ]);
      setSiteRates(rates);
      setSiteTypes(types.filter(t => t.active));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load site rates');
      console.error('Failed to load site rates:', err);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  // Fetch site rates and site types on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (rateId: number, description: string) => {
    if (!window.confirm(`Are you sure you want to delete "${description || 'this rate'}"? This cannot be undone.`)) {
      return;
    }
    try {
      await configService.deleteStorePricing(rateId);
      setSiteRates(siteRates.filter(rate => rate.id !== rateId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete site rate');
    }
  };

  const openAddModal = async () => {
    setEditingRate(null);
    setFormData({
      site_type_id: undefined,
      description: '',
      price: 0,
      discount: 0,
      effective_date: '',
      end_date: '',
      active: true,
    });
    // Re-fetch site types to get any newly added types
    try {
      const types = await siteService.getStoreSiteTypes(storeId);
      setSiteTypes(types.filter(t => t.active));
    } catch (err) {
      console.error('Failed to refresh site types:', err);
    }
    setShowModal(true);
  };

  const openEditModal = (rate: StorePricing) => {
    setEditingRate(rate);
    setFormData({
      site_type_id: rate.site_type_id,
      description: rate.description || '',
      price: rate.price,
      discount: rate.discount || 0,
      effective_date: rate.effective_date ? rate.effective_date.split('T')[0] : '',
      end_date: rate.end_date ? rate.end_date.split('T')[0] : '',
      active: rate.active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.price || formData.price <= 0) {
      alert('Please enter a valid price');
      return;
    }

    setSaving(true);
    try {
      if (editingRate) {
        // Update existing rate
        const updateData: StorePricingUpdate = {
          site_type_id: formData.site_type_id,
          description: formData.description || undefined,
          price: formData.price,
          discount: formData.discount,
          effective_date: formData.effective_date || undefined,
          end_date: formData.end_date || undefined,
          active: formData.active,
        };
        const updated = await configService.updateStorePricing(editingRate.id, updateData);
        setSiteRates(siteRates.map(r => r.id === editingRate.id ? updated : r));
      } else {
        // Create new rate
        if (!storeId) {
          alert('Store ID not found. Please log in again.');
          return;
        }
        const createData: StorePricingCreate = {
          store_id: storeId,
          site_type_id: formData.site_type_id,
          description: formData.description || undefined,
          price: formData.price,
          discount: formData.discount,
          effective_date: formData.effective_date || undefined,
          end_date: formData.end_date || undefined,
          active: formData.active,
        };
        const created = await configService.createStorePricing(createData);
        setSiteRates([...siteRates, created]);
      }
      setShowModal(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save site rate');
    } finally {
      setSaving(false);
    }
  };

  // Rate dates are calendar days (YYYY-MM-DD); dateUtils keeps them local.
  const formatOptionalDate = (dateStr?: string) => {
    return dateStr ? formatDate(dateStr) : '-';
  };

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">$</span>
          <h2>Site Rates</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">campground_db/store_pricing</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            {String.fromCharCode(9660)}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          {loading ? (
            <div className="loading-state">Loading site rates...</div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-primary btn-sm" onClick={loadData}>
                Retry
              </button>
            </div>
          ) : siteRates.length === 0 ? (
            <div className="empty-state">
              <p>No site rates configured yet.</p>
            </div>
          ) : (
            <table className="config-table">
              <thead>
                <tr>
                  <th>Site Type</th>
                  <th>Description</th>
                  <th>Price</th>
                  <th>Discount</th>
                  <th>Effective Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {siteRates.map(rate => (
                  <tr key={rate.id}>
                    <td>{rate.site_type_name || 'All Types'}</td>
                    <td>{rate.description || `Rate #${rate.id}`}</td>
                    <td className="amount">{formatCurrency(rate.price)}</td>
                    <td className="amount">{rate.discount}%</td>
                    <td>{formatOptionalDate(rate.effective_date)}</td>
                    <td>{formatOptionalDate(rate.end_date)}</td>
                    <td className={rate.active ? 'status-active' : 'status-inactive'}>
                      {rate.active ? 'Active' : 'Inactive'}
                    </td>
                    <td>
                      <div className="config-actions">
                        <button
                          className="btn btn-text btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(rate);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-text btn-sm btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(rate.id, rate.description || '');
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="config-card-footer">
            <button className="btn btn-primary btn-sm" onClick={openAddModal}>
              + Add Site Rate
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRate ? 'Edit Site Rate' : 'Add Site Rate'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                {String.fromCharCode(215)}
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Site Type</label>
                <select
                  value={formData.site_type_id || ''}
                  onChange={(e) => setFormData({ ...formData, site_type_id: e.target.value ? Number(e.target.value) : undefined })}
                >
                  <option value="">All Site Types (Default)</option>
                  {siteTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Standard Rate, Weekend Rate, Holiday Rate"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price || ''}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group">
                  <label>Discount (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.discount || ''}
                    onChange={(e) => setFormData({ ...formData, discount: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Effective Date</label>
                  <input
                    type="date"
                    value={formData.effective_date}
                    onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteRateCard;
